import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Camera, Stethoscope, BookOpen } from "lucide-react";
import api from "../lib/api";
import ZoomableImage from "../components/site/ImageLightbox";
import RichContent from "../components/site/RichContent";
import AuthorCard from "../components/site/AuthorCard";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { AUTHOR_REF, AUTHOR_REF_MANISHA, PUBLISHER_REF, SITE, breadcrumb, graph } from "../lib/schema";

const DOCTOR_META = {
  shantanu: {
    slug: "shantanu",
    name: "Dr. Shantanu Kumar Gupta",
    href: "/about",
    schemaRef: null, // resolved lazily below
  },
  manisha: {
    slug: "manisha",
    name: "Dr. Manisha Mishra",
    href: "/team/dr-manisha-mishra",
    schemaRef: null,
  },
  team: {
    slug: "team",
    name: "Shantanu Netralaya clinical team",
    href: "/about",
    schemaRef: null,
  },
};

const EyeCaseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const canonicalPath = `/eye-cases/${slug}`;

  useEffect(() => {
    setC(null);
    setNotFound(false);
    api
      .get(`/cases/${slug}`)
      .then((r) => {
        // If backend resolved a legacy slug, silently rewrite the URL to canonical.
        if (r.data?.canonical_slug && r.data.canonical_slug !== slug) {
          navigate(`/eye-cases/${r.data.canonical_slug}`, { replace: true });
          return;
        }
        setC(r.data);
      })
      .catch(() => setNotFound(true));
  }, [slug, navigate]);

  usePageMeta({
    title: c ? `${c.title} — Clinical Case | Dr. Shantanu Kumar Gupta` : "Clinical Case — Dr. Shantanu Kumar Gupta",
    path: canonicalPath,
    description: c ? (c.summary || `${c.category} case study by Dr. Shantanu Kumar Gupta, Varanasi.`) : "",
    image: c?.image_url || undefined,
  });

  const caseSchema = useMemo(() => {
    if (!c) return null;
    const url = `${SITE}${canonicalPath}`;
    const submittedBy = (c.submitted_by || "shantanu").toLowerCase();
    const authorRef = submittedBy === "manisha" ? AUTHOR_REF_MANISHA : AUTHOR_REF;
    const nodes = [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Eye Case Studies", path: "/eye-cases" },
        { name: c.title, path: canonicalPath },
      ]),
      // MedicalWebPage schema — signals to Google this is a doctor-authored,
      // medically-reviewed case study (as opposed to a generic blog post).
      {
        "@type": "MedicalWebPage",
        "@id": `${url}#page`,
        url,
        name: c.title,
        description: c.summary,
        inLanguage: "en-IN",
        author: authorRef,
        reviewedBy: authorRef,
        publisher: PUBLISHER_REF,
        datePublished: c.created_at,
        dateModified: c.updated_at,
        about: c.diagnosis
          ? { "@type": "MedicalCondition", name: c.diagnosis }
          : undefined,
        specialty: {
          "@type": "MedicalSpecialty",
          name: "Ophthalmology",
        },
      },
    ];
    if (c.image_url) {
      nodes.push({
        "@type": "ImageObject",
        "@id": `${url}#hero-image`,
        contentUrl: c.image_url,
        url: c.image_url,
        caption: c.image_alt || c.title,
        creditText: `${DOCTOR_META[submittedBy]?.name || "Shantanu Netralaya"}, Shantanu Netralaya Eye Hospital, Varanasi`,
        creator: authorRef,
        copyrightNotice:
          "© Shantanu Netralaya. Anonymised clinical photograph shared for educational use.",
        license: "https://creativecommons.org/licenses/by-nc-nd/4.0/",
      });
    }
    return graph(nodes.filter(Boolean));
  }, [c, canonicalPath]);
  usePageJsonLd(caseSchema);

  if (notFound) {
    return (
      <div className="container-page py-24 text-center" data-testid="case-not-found">
        <h1 className="font-serif text-3xl">Case not found</h1>
        <p className="mt-3 text-muted-foreground">
          The case may have been unpublished or the URL is incorrect.
        </p>
        <Link to="/eye-cases" className="mt-6 inline-flex btn-primary text-xs">
          <ArrowLeft size={14} /> Back to all case studies
        </Link>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="container-page py-24 text-center text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  const externalLinks = (c.external_links || []).filter((l) => l && l.url);
  const extraImages = (c.extra_images || []).filter((im) => im && im.url);
  const submittedBy = (c.submitted_by || "shantanu").toLowerCase();
  const doctor = DOCTOR_META[submittedBy] || DOCTOR_META.shantanu;
  const authorCardSlug = submittedBy === "manisha" ? "manisha" : "shantanu";

  return (
    <div data-testid="case-detail-page">
      <section className="pt-16 pb-4 container-page">
        <Link
          to="/eye-cases"
          className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] inline-flex items-center gap-1.5"
          data-testid="case-back-link"
        >
          <ArrowLeft size={12} /> All case studies
        </Link>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="overline inline-flex items-center gap-1.5">
            <Camera size={11} /> {c.category}
          </span>
          {c.diagnosis && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--primary))]/8 text-[11px] text-[hsl(var(--primary))]">
              <Stethoscope size={11} /> Dx · {c.diagnosis}
            </span>
          )}
        </div>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-[1.1] text-balance max-w-4xl">
          {c.title}
        </h1>
        {c.summary && (
          <p className="mt-5 max-w-3xl prose-elegant">{c.summary}</p>
        )}
        {c.patient_context && (
          <p className="mt-3 text-sm text-muted-foreground italic max-w-3xl">
            Patient context — {c.patient_context}
          </p>
        )}
        <div className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground" data-testid="case-byline">
          Submitted by
          <Link to={doctor.href} className="text-[hsl(var(--primary))] font-medium normal-case tracking-normal text-sm hover:underline">
            {doctor.name}
          </Link>
        </div>
      </section>

      {c.image_url && (
        <section className="container-page pb-10">
          <figure
            className="rounded-2xl overflow-hidden border border-border bg-black flex justify-center"
            data-testid="case-hero-image"
          >
            <ZoomableImage
              src={c.image_url}
              alt={c.image_alt || c.title}
              caption={c.image_alt}
              className="max-w-full"
              imgClassName="block w-auto max-w-full max-h-[75vh] object-contain mx-auto"
              testId="case-hero-zoomable"
            />
          </figure>
          {c.image_alt && (
            <div className="text-xs text-muted-foreground text-center pt-3 max-w-3xl mx-auto">
              {c.image_alt}
            </div>
          )}
        </section>
      )}

      <section className="container-page pb-16 grid lg:grid-cols-[1fr_320px] gap-12">
        <article className="min-w-0">
          {c.description && <RichContent text={c.description} />}

          {extraImages.length > 0 && (
            <section className="mt-14" data-testid="case-extra-images">
              <h2 className="font-serif text-2xl mb-5">Additional images</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {extraImages.map((im, idx) => (
                  <figure
                    key={`${im.url}-${idx}`}
                    className="rounded-xl overflow-hidden border border-border bg-black/5"
                  >
                    <ZoomableImage
                      src={im.url}
                      alt={im.caption || `Case image ${idx + 1}`}
                      caption={im.caption}
                      imgClassName="block w-full h-auto"
                      testId={`case-extra-zoomable-${idx}`}
                    />
                    {im.caption && (
                      <figcaption className="text-xs text-muted-foreground text-center py-2 px-3 bg-[hsl(var(--secondary))]/30">
                        {im.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </section>
          )}

          {externalLinks.length > 0 && (
            <section className="mt-14 rounded-2xl border border-border p-6 bg-[hsl(var(--secondary))]/20" data-testid="case-external-links">
              <h2 className="font-serif text-xl mb-4 inline-flex items-center gap-2">
                <BookOpen size={16} className="text-[hsl(var(--accent))]" /> Further reading & references
              </h2>
              <ul className="space-y-2">
                {externalLinks.map((l, idx) => (
                  <li key={`${l.url}-${idx}`}>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-sm text-[hsl(var(--primary))] hover:underline inline-flex items-start gap-2"
                      data-testid={`case-external-link-${idx}`}
                    >
                      <ExternalLink size={13} className="mt-0.5 shrink-0" />
                      <span>{l.label || l.url}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <MedicalDisclaimer />
        </article>

        <aside className="lg:sticky lg:top-24 self-start space-y-6">
          <AuthorCard doctorSlug={authorCardSlug} />
          {(c.related_expertise || []).length > 0 && (
            <div className="rounded-2xl border border-border p-6 bg-background">
              <div className="overline mb-3">Related expertise</div>
              <ul className="space-y-2">
                {c.related_expertise.map((s) => (
                  <li key={s}>
                    <Link
                      to={`/expertise/${s}`}
                      className="text-sm text-[hsl(var(--primary))] hover:underline"
                      data-testid={`case-related-expertise-${s}`}
                    >
                      {s.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())} →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
};

export default EyeCaseDetail;
